// ═══════════════════════════════════════════════════════════════
// SHELTER — Public Pre-Registration (no auth required)
// Allows individuals to pre-register themselves + family members
// before arriving at a shelter. Status = 'pre-registered'
// ═══════════════════════════════════════════════════════════════

import { useState, useRef } from 'react';
import {
  Box, Paper, Title, Text, TextInput, Select, Textarea, Button, Autocomplete,
  Group, Stack, SimpleGrid, Divider, Badge, ThemeIcon,
} from '../../components/gab/GabComponents';
import { IconShieldCheck, IconPlus, IconTrash, IconUserPlus } from '@tabler/icons-react';
import { createRecord, updateRecord, TABLES, FIELDS, WORKER_URL } from '../../lib/api-switch';
import { primary } from '../../lib/colors';

const WORKER = WORKER_URL;

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

interface FamilyMember {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  specialNeeds: string;
  medications: string;
  isMinor: boolean;
}

const EMPTY_MEMBER: FamilyMember = {
  firstName: '', lastName: '', dateOfBirth: '', gender: '',
  specialNeeds: '', medications: '', isMinor: false,
};

export default function PublicPreRegister() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [homeAddress, setHomeAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [specialNeeds, setSpecialNeeds] = useState('');
  const [dietaryNeeds, setDietaryNeeds] = useState('');
  const [medications, setMedications] = useState('');
  const [emergencyContactName, setEmergencyContactName] = useState('');
  const [emergencyContactPhone, setEmergencyContactPhone] = useState('');
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Address autocomplete
  const [addrSuggestions, setAddrSuggestions] = useState<string[]>([]);
  const addrTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleAddrSearch = (q: string) => {
    setHomeAddress(q);
    if (addrTimeout.current) clearTimeout(addrTimeout.current);
    if (q.length < 3) { setAddrSuggestions([]); return; }
    addrTimeout.current = setTimeout(async () => {
      try {
        const r = await fetch(`${WORKER}/api/here/autosuggest?q=${encodeURIComponent(q)}&at=33.749,-84.388&limit=5`, {
          headers: { 'Origin': 'https://apps.opengov.com' },
        });
        if (!r.ok) return;
        const data = await r.json();
        setAddrSuggestions((data.items || [])
          .filter((i: any) => i.address?.label)
          .map((i: any) => i.address.label));
      } catch { /* ignore */ }
    }, 300);
  };
  const [regCode, setRegCode] = useState('');

  const addFamilyMember = () => setFamilyMembers(prev => [...prev, { ...EMPTY_MEMBER }]);
  const removeFamilyMember = (idx: number) => setFamilyMembers(prev => prev.filter((_, i) => i !== idx));
  const updateMember = (idx: number, field: keyof FamilyMember, value: string | boolean) => {
    setFamilyMembers(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  };

  const handleSubmit = async () => {
    if (!firstName.trim() || !lastName.trim()) return;
    setSubmitting(true);
    try {
      const F = FIELDS.survivors;
      const code = `PRE-${Date.now().toString(36).toUpperCase()}`;
      // Create primary registrant
      const fields: Array<[string, string]> = [
        [F.firstName, firstName.trim()],
        [F.lastName, lastName.trim()],
        [F.phone, phone],
        [F.email, email],
        [F.homeAddress, homeAddress],
        [F.city, city],
        [F.state, state],
        [F.zip, zip],
        [F.specialNeeds, specialNeeds],
        [F.status, 'pre-registered'],
      ];
      const rid = await createRecord(TABLES.survivors, fields, { anonymous: true });
      // PUT remaining fields (POST max 10)
      if (rid) {
        const extra: Array<[string, string]> = [
          [F.dietaryNeeds, dietaryNeeds],
          [F.medications, medications],
          [F.emergencyContactName, emergencyContactName],
          [F.emergencyContactPhone, emergencyContactPhone],
          [F.qrCode, code],
        ];
        await updateRecord(TABLES.survivors, rid, extra, { anonymous: true });
      }

      // Create family members
      for (const member of familyMembers) {
        if (!member.firstName.trim()) continue;
        const mFields: Array<[string, string]> = [
          [F.firstName, member.firstName.trim()],
          [F.lastName, member.lastName.trim() || lastName.trim()],
          [F.dateOfBirth, member.dateOfBirth],
          [F.gender, member.gender],
          [F.specialNeeds, member.specialNeeds],
          [F.medications, member.medications],
          [F.isMinor, member.isMinor ? 'true' : 'false'],
          [F.status, 'pre-registered'],
        ];
        await createRecord(TABLES.survivors, mFields, { anonymous: true });
      }

      setRegCode(code);
      setSubmitted(true);
    } catch (err) {
      console.error('Pre-registration failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Box mih="100vh" style={{
        background: 'linear-gradient(135deg, var(--color-primary-900) 0%, var(--color-primary-700) 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}>
        <Paper shadow="lg" radius="md" p="xl" maw={500} w="100%">
          <Stack align="center" gap="md">
            <ThemeIcon size={64} radius="xl" variant="light" color="green">
              <IconShieldCheck size={36} />
            </ThemeIcon>
            <Title order={3} ta="center" c="#07963F">Pre-Registration Complete</Title>
            <Text ta="center" c="dimmed">
              {firstName} {lastName} and {familyMembers.length} family member(s) have been pre-registered.
            </Text>
            <Paper p="md" bg="gray.0" radius="md" withBorder w="100%">
              <Text size="xs" c="dimmed" ta="center">Your Registration Code</Text>
              <Text fw={800} size="xl" ta="center" ff="monospace" c={primary[900]} mt={4}>
                {regCode}
              </Text>
              <Text size="xs" c="dimmed" ta="center" mt={4}>
                Show this code when you arrive at the shelter for quick check-in.
              </Text>
            </Paper>
            <Button variant="light" onClick={() => { setSubmitted(false); setFirstName(''); setLastName(''); setFamilyMembers([]); }}>
              Register Another
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box mih="100vh" style={{
      background: 'linear-gradient(135deg, #f0f4f8 0%, #d9e2ec 100%)',
      padding: 24,
    }}>
      <Paper shadow="md" radius="md" p="xl" maw={700} mx="auto">
        <Stack align="center" gap="xs" mb="lg">
          <ThemeIcon size={48} radius="xl" variant="light" color="indigo">
            <IconUserPlus size={24} />
          </ThemeIcon>
          <Title order={3} c={primary[900]}>Shelter Pre-Registration</Title>
          <Text size="sm" c="dimmed" ta="center" maw={500}>
            Pre-register yourself and your family before arriving at the shelter.
            This speeds up the check-in process when you arrive.
          </Text>
        </Stack>

        <Divider mb="md" label="Primary Registrant" labelPosition="center" />

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" mb="md">
          <TextInput label="First Name *" value={firstName} onChange={e => setFirstName(e.target.value)} required />
          <TextInput label="Last Name *" value={lastName} onChange={e => setLastName(e.target.value)} required />
          <TextInput label="Phone" value={phone} placeholder="(555) 123-4567"
            onChange={e => setPhone(formatPhone(e.target.value))} />
          <TextInput label="Email" value={email} onChange={e => setEmail(e.target.value)} />
          <Autocomplete label="Home Address" value={homeAddress}
            onChange={handleAddrSearch} onOptionSubmit={setHomeAddress}
            data={addrSuggestions} placeholder="Start typing an address..." />
          <TextInput label="City" value={city} onChange={e => setCity(e.target.value)} />
          <TextInput label="State" value={state} onChange={e => setState(e.target.value)} />
          <TextInput label="ZIP" value={zip} onChange={e => setZip(e.target.value)} />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" mb="md">
          <TextInput label="Emergency Contact Name" value={emergencyContactName}
            onChange={e => setEmergencyContactName(e.target.value)} />
          <TextInput label="Emergency Contact Phone" value={emergencyContactPhone} placeholder="(555) 123-4567"
            onChange={e => setEmergencyContactPhone(formatPhone(e.target.value))} />
        </SimpleGrid>

        <Textarea label="Special Needs" value={specialNeeds}
          onChange={e => setSpecialNeeds(e.target.value)} autosize minRows={1} mb="sm" />
        <Textarea label="Dietary Needs" value={dietaryNeeds}
          onChange={e => setDietaryNeeds(e.target.value)} autosize minRows={1} mb="sm" />
        <Textarea label="Medications" value={medications}
          onChange={e => setMedications(e.target.value)} autosize minRows={1} mb="md" />

        {/* Family Members */}
        <Divider mb="md" label={`Family Members (${familyMembers.length})`} labelPosition="center" />

        {familyMembers.map((member, idx) => (
          <Paper key={idx} p="md" withBorder radius="sm" mb="sm" style={{ background: 'var(--mantine-color-gray-0)' }}>
            <Group justify="space-between" mb="xs">
              <Badge size="sm" variant="light">Family Member {idx + 1}</Badge>
              <Button variant="subtle" size="compact-xs" color="red" leftSection={<IconTrash size={12} />}
                onClick={() => removeFamilyMember(idx)}>Remove</Button>
            </Group>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
              <TextInput label="First Name" size="xs" value={member.firstName}
                onChange={e => updateMember(idx, 'firstName', e.target.value)} />
              <TextInput label="Last Name" size="xs" value={member.lastName}
                onChange={e => updateMember(idx, 'lastName', e.target.value)}
                placeholder={lastName || 'Same as primary'} />
              <TextInput label="Date of Birth" type="date" size="xs" value={member.dateOfBirth}
                onChange={e => updateMember(idx, 'dateOfBirth', e.target.value)} />
              <Select label="Gender" size="xs" data={[
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
                { value: 'other', label: 'Other' },
              ]} value={member.gender} onChange={v => updateMember(idx, 'gender', v ?? '')} clearable />
            </SimpleGrid>
            <TextInput label="Special Needs / Medications" size="xs" mt="xs"
              value={member.specialNeeds} onChange={e => updateMember(idx, 'specialNeeds', e.target.value)} />
          </Paper>
        ))}

        <Button variant="light" fullWidth leftSection={<IconPlus size={14} />} onClick={addFamilyMember} mb="lg">
          Add Family Member
        </Button>

        <Button fullWidth size="lg" onClick={handleSubmit} loading={submitting}
          disabled={!firstName.trim() || !lastName.trim()}>
          Submit Pre-Registration
        </Button>

        <Text size="xs" c="dimmed" ta="center" mt="md">
          OpenGov Emergency Management Center
        </Text>
      </Paper>
    </Box>
  );
}
